/**
 * React Query mutation hook for restoring a config snapshot.
 *
 * Drives the two-step Restore flow on the backups table: first called with
 * `dryRun: true` to preview the `RestoreReport` inside the confirmation modal,
 * then with `dryRun: false` on confirm to apply the merge-only restore. Only a
 * real restore invalidates the cached lists.
 *
 * A real restore writes config versions and captures a `pre_restore` snapshot,
 * so both the history list and the snapshot list are invalidated.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  configVersioningService,
  type RestoreReport,
} from '../../api/configVersioningService';
import { configVersioningKeys } from './queryKeys';

export interface RestoreSnapshotVars {
  snapshotId: string;
  dryRun: boolean;
}

export function useRestoreSnapshot(): UseMutationResult<
  RestoreReport,
  Error,
  RestoreSnapshotVars
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ snapshotId, dryRun }: RestoreSnapshotVars) =>
      configVersioningService.restoreSnapshot(snapshotId, dryRun),
    onSuccess: (_data, variables) => {
      if (variables.dryRun) return;
      void queryClient.invalidateQueries({
        queryKey: configVersioningKeys.snapshotList(),
      });
      void queryClient.invalidateQueries({
        queryKey: configVersioningKeys.historyList(),
      });
    },
  });
}
